import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { UsStateSelect } from '~/components/forms/UsStateSelect';
import { SlideInPanel } from '~/components/SlideInPanel';
import {
  CALENDAR_OUTLINE_BUTTON,
  CALENDAR_PRIMARY_BUTTON,
} from '~/features/shared/calendar/controlStyles';
import { formatDisplayDate } from '~/features/shared/calendar/dateUtils';
import { SHIFT_TEMPLATES, STATUS_LABEL } from '../constants';
import { buildShiftTimestamps, timestampToLocalClock } from '../dateTime';
import type {
  AvailabilityConflict,
  AvailabilityDay,
  AvailabilityScope,
  CalendarContract,
  ShiftLabel,
  ShiftType,
  WorkLocation,
  WritableAvailabilityEntry,
  WritableBatchDay,
} from '../types';
import { scopeServiceListingId } from '../types';
import { CalendarContractList } from './CalendarContractList';

/** First few dates, formatted, plus a rollup of the rest ("Jan 4, Jan 5, and 3 more"). */
const MAX_SUMMARIZED_DATES = 3;
const summarizeDates = (dates: string[]): string => {
  const shown = dates.slice(0, MAX_SUMMARIZED_DATES).map(formatDisplayDate);
  const remaining = dates.length - shown.length;
  return remaining > 0 ? `${shown.join(', ')}, and ${remaining} more` : shown.join(', ');
};

interface DetailDraft {
  clientId: string;
  // Set only for a draft loaded from an existing entry. Sending it back lets a
  // recurrence-generated occurrence keep its rule link across an edit.
  entryId?: number;
  endTime: string;
  notes: string;
  serviceListingId?: number | null;
  shiftLabel: ShiftLabel;
  shiftType: ShiftType;
  startTime: string;
  stateCode: string;
  timezone: string;
  workLocation: WorkLocation | '';
  rateOverrideType: WritableAvailabilityEntry['rateOverrideType'];
  pricePerHourOverride: WritableAvailabilityEntry['pricePerHourOverride'];
  pricePerShiftOverride: WritableAvailabilityEntry['pricePerShiftOverride'];
  pricePerCallShiftOverride: WritableAvailabilityEntry['pricePerCallShiftOverride'];
  status: 'AVAILABLE' | 'UNAVAILABLE';
}

interface DateOnlyDraft {
  entryId?: number;
  notes: string;
  pricePerCallShiftOverride: WritableAvailabilityEntry['pricePerCallShiftOverride'];
  pricePerHourOverride: WritableAvailabilityEntry['pricePerHourOverride'];
  pricePerShiftOverride: WritableAvailabilityEntry['pricePerShiftOverride'];
  rateOverrideType: WritableAvailabilityEntry['rateOverrideType'];
  serviceListingId?: number | null;
  stateCode: string;
  status: 'AVAILABLE' | 'UNAVAILABLE';
  timezone: string;
  workLocation: WorkLocation | '';
}

interface AvailabilityEditorPanelProps {
  busy: boolean;
  conflict?: AvailabilityConflict | null;
  /**
   * [MCV-1844] Contracts covering the date(s) the editor is open on, in any status. Listed with a
   * `View contract` action when `onViewContract` is given.
   */
  contracts?: readonly CalendarContract[];
  /** The date(s) the editor is open against. Empty means closed. */
  dates: string[];
  /**
   * The single loaded day to pre-fill from. Only ever set (by the caller)
   * when `editorMode === 'single'` -- a template editor spans dates that were
   * never all loaded as one day, so it always starts blank.
   */
  day: AvailabilityDay | null;
  /**
   * 'single': one in-range date, pre-filled from `day`, saved with `onSave`
   * (a PUT that fully replaces that one day). 'template': two or more dates,
   * or the one date that fell outside the loaded range -- starts blank and
   * saves every listed date at once with `onSaveDays`.
   */
  editorMode: 'single' | 'template';
  mutationError?: string | null;
  onClear: () => void;
  onClose: () => void;
  onSave: (entries: WritableAvailabilityEntry[]) => void;
  onSaveDays: (days: WritableBatchDay[]) => void;
  /** Opens a contract's page; without it the contract list and locked-entry links are hidden. */
  onViewContract?: (contractId: number) => void;
  scope: AvailabilityScope;
  timezone?: string | null;
}

const isLocked = (status: string) => status === 'HELD' || status === 'BOOKED';

export const AvailabilityEditorPanel = ({
  busy,
  conflict = null,
  contracts = [],
  dates,
  day,
  editorMode,
  mutationError = null,
  onClear,
  onClose,
  onSave,
  onSaveDays,
  onViewContract,
  scope,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null,
}: AvailabilityEditorPanelProps) => {
  const nextDraftId = useRef(1);
  const [mode, setMode] = useState<'DATE_ONLY' | 'SHIFT_DETAIL'>('DATE_ONLY');
  const [drafts, setDrafts] = useState<DetailDraft[]>([]);
  const [dateOnlyDraft, setDateOnlyDraft] = useState<DateOnlyDraft>({
    notes: '',
    pricePerCallShiftOverride: null,
    pricePerHourOverride: null,
    pricePerShiftOverride: null,
    rateOverrideType: null,
    serviceListingId: scopeServiceListingId(scope),
    stateCode: '',
    status: 'AVAILABLE',
    timezone: timezone ?? 'UTC',
    workLocation: '',
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const entries = day?.entries ?? [];
  const lockedEntries = entries.filter((entry) => isLocked(entry.status));
  const editingDisabled = busy || scope.kind === 'all';

  useEffect(() => {
    const editableDetailed = (day?.entries ?? []).filter(
      (entry) => !isLocked(entry.status) && entry.detailMode === 'SHIFT_DETAIL'
    );
    setMode(editableDetailed.length ? 'SHIFT_DETAIL' : 'DATE_ONLY');
    setDrafts(
      editableDetailed.map((entry) => ({
        clientId: `entry-${entry.id}`,
        entryId: entry.id,
        endTime: timestampToLocalClock(entry.endsAt, entry.timezone),
        notes: entry.notes ?? '',
        serviceListingId: entry.serviceListingId,
        shiftLabel: entry.shiftLabel ?? 'DAY',
        shiftType: entry.shiftType ?? 'SHIFT',
        startTime: timestampToLocalClock(entry.startsAt, entry.timezone),
        stateCode: entry.stateCode ?? '',
        timezone: entry.timezone,
        workLocation: entry.workLocation ?? '',
        rateOverrideType: entry.rateOverrideType,
        pricePerHourOverride: entry.pricePerHourOverride,
        pricePerShiftOverride: entry.pricePerShiftOverride,
        pricePerCallShiftOverride: entry.pricePerCallShiftOverride,
        status: entry.status === 'UNAVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
      }))
    );
    const editableDateOnly = (day?.entries ?? []).find(
      (entry) => !isLocked(entry.status) && entry.detailMode === 'DATE_ONLY'
    );
    setDateOnlyDraft({
      entryId: editableDateOnly?.id,
      notes: editableDateOnly?.notes ?? '',
      pricePerCallShiftOverride: editableDateOnly?.pricePerCallShiftOverride ?? null,
      pricePerHourOverride: editableDateOnly?.pricePerHourOverride ?? null,
      pricePerShiftOverride: editableDateOnly?.pricePerShiftOverride ?? null,
      rateOverrideType: editableDateOnly?.rateOverrideType ?? null,
      serviceListingId: editableDateOnly?.serviceListingId ?? scopeServiceListingId(scope),
      stateCode: editableDateOnly?.stateCode ?? '',
      status: editableDateOnly?.status === 'UNAVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
      timezone: editableDateOnly?.timezone ?? timezone ?? 'UTC',
      workLocation: editableDateOnly?.workLocation ?? '',
    });
    setValidationError(null);
    // `day` is only ever non-null in single mode (the caller enforces that),
    // so this already resets to a blank draft whenever a template editor
    // opens -- `dates` is the trigger for that (a new target set), `day` for
    // a single-mode date's data arriving/changing.
  }, [dates, day, scope]);

  const addDraft = () => {
    const template = SHIFT_TEMPLATES.DAY;
    setMode('SHIFT_DETAIL');
    setDrafts((current) => [
      ...current,
      {
        clientId: `draft-${nextDraftId.current++}`,
        endTime: template.endTime,
        notes: '',
        serviceListingId: scopeServiceListingId(scope),
        shiftLabel: 'DAY',
        shiftType: 'SHIFT',
        startTime: template.startTime,
        stateCode: '',
        workLocation: '',
        rateOverrideType: null,
        pricePerHourOverride: null,
        pricePerShiftOverride: null,
        pricePerCallShiftOverride: null,
        status: 'AVAILABLE',
        timezone: timezone ?? 'UTC',
      },
    ]);
  };

  const updateDraft = (clientId: string, values: Partial<DetailDraft>) =>
    setDrafts((current) =>
      current.map((draft) => (draft.clientId === clientId ? { ...draft, ...values } : draft))
    );

  // Shared by both modes: single-day save calls this once for `dates[0]`;
  // template save calls it once per target date so each date's SHIFT_DETAIL
  // timestamps anchor to its own calendar date. Template mode's drafts never
  // carry an `entryId` (the prefill effect above never seeds one for a blank
  // draft), so this naturally sends no entry ids in that mode.
  const buildEntriesForDate = (targetDate: string): WritableAvailabilityEntry[] => {
    if (mode === 'DATE_ONLY') {
      const serviceListingId = dateOnlyDraft.serviceListingId;
      return [
        {
          detailMode: 'DATE_ONLY',
          ...(dateOnlyDraft.entryId !== undefined ? { id: dateOnlyDraft.entryId } : {}),
          status: dateOnlyDraft.status,
          timezone: dateOnlyDraft.timezone,
          ...(serviceListingId !== undefined ? { serviceListingId } : {}),
          workLocation: dateOnlyDraft.workLocation || null,
          stateCode: dateOnlyDraft.stateCode.trim().toUpperCase() || null,
          rateOverrideType: dateOnlyDraft.rateOverrideType,
          pricePerHourOverride: dateOnlyDraft.pricePerHourOverride,
          pricePerShiftOverride: dateOnlyDraft.pricePerShiftOverride,
          pricePerCallShiftOverride: dateOnlyDraft.pricePerCallShiftOverride,
          notes: dateOnlyDraft.notes.trim() || null,
        },
      ];
    }
    if (drafts.length === 0) throw new Error('Add at least one shift window.');
    return drafts.map((draft) => {
      const timestamps = buildShiftTimestamps(
        targetDate,
        draft.startTime,
        draft.endTime,
        draft.timezone
      );
      return {
        detailMode: 'SHIFT_DETAIL',
        ...(draft.entryId !== undefined ? { id: draft.entryId } : {}),
        ...timestamps,
        timezone: draft.timezone,
        serviceListingId: draft.serviceListingId,
        shiftLabel: draft.shiftLabel,
        shiftType: draft.shiftType,
        workLocation: draft.workLocation || null,
        stateCode: draft.stateCode.trim().toUpperCase() || null,
        status: draft.status,
        rateOverrideType: draft.rateOverrideType,
        pricePerHourOverride: draft.pricePerHourOverride,
        pricePerShiftOverride: draft.pricePerShiftOverride,
        pricePerCallShiftOverride: draft.pricePerCallShiftOverride,
        notes: draft.notes.trim() || null,
      };
    });
  };

  const handleSave = () => {
    if (dates.length === 0) return;
    setValidationError(null);
    try {
      if (editorMode === 'template') {
        onSaveDays(
          dates.map((targetDate) => ({
            date: targetDate,
            entries: buildEntriesForDate(targetDate),
          }))
        );
        return;
      }
      onSave(buildEntriesForDate(dates[0]));
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Check the shift details.');
    }
  };

  return (
    <SlideInPanel
      modal={false}
      onClose={onClose}
      open={dates.length > 0}
      title={
        dates.length === 0
          ? 'Availability'
          : editorMode === 'single'
            ? `Availability · ${formatDisplayDate(dates[0])}`
            : `Edit availability for ${dates.length} dates`
      }
      width="max-w-xl"
    >
      <div aria-busy={busy} className="space-y-5" data-cy="provider-calendar-editor">
        {scope.kind === 'all' ? (
          <p className="rounded-md bg-warning-50 p-3 text-sm text-warning-950">
            You are editing all availability scopes. Choose General or a service listing to make a
            scope-specific change.
          </p>
        ) : null}

        {editorMode === 'template' ? (
          <div
            className="space-y-2 rounded-md bg-grey-950 p-3 text-sm"
            data-cy="provider-calendar-editor-template-summary"
          >
            <p className="text-grey-400">{summarizeDates(dates)}</p>
            <p className="text-warning-950">
              Saving replaces the editable availability on all {dates.length} selected dates. Held
              or booked entries on those dates are left as they are — if a change would conflict
              with one, none of the {dates.length} dates are saved.
            </p>
          </div>
        ) : null}

        {onViewContract && contracts.length ? (
          <section data-cy="provider-calendar-editor-contracts">
            <h3 className="body-text-sb text-grey-100">Contracts</h3>
            <div className="mt-2">
              <CalendarContractList
                contracts={contracts}
                cyPrefix="provider-calendar-editor-contract"
                onViewContract={onViewContract}
              />
            </div>
          </section>
        ) : null}

        {lockedEntries.length ? (
          <section>
            <h3 className="body-text-sb text-grey-100">Locked commitments</h3>
            <div className="mt-2 space-y-2">
              {lockedEntries.map((entry) => (
                <div className="rounded-md border border-grey-900 bg-grey-950 p-3" key={entry.id}>
                  <p className="body-text-sb text-grey-100">{STATUS_LABEL[entry.status]}</p>
                  <p className="mt-1 text-xs text-grey-500">
                    This system-managed entry cannot be changed from the calendar.
                  </p>
                  {onViewContract && entry.bookedContractId != null ? (
                    <button
                      className={`${CALENDAR_OUTLINE_BUTTON} mt-2`}
                      data-cy={`provider-calendar-locked-contract-${entry.bookedContractId}`}
                      onClick={() => onViewContract(entry.bookedContractId as number)}
                      type="button"
                    >
                      View contract
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <fieldset disabled={editingDisabled}>
          <legend className="body-text-sb text-grey-100">Availability detail</legend>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              aria-pressed={mode === 'DATE_ONLY'}
              className={mode === 'DATE_ONLY' ? CALENDAR_PRIMARY_BUTTON : CALENDAR_OUTLINE_BUTTON}
              data-cy="provider-calendar-editor-date-only"
              onClick={() => setMode('DATE_ONLY')}
              type="button"
            >
              Date only
            </button>
            <button
              aria-pressed={mode === 'SHIFT_DETAIL'}
              className={
                mode === 'SHIFT_DETAIL' ? CALENDAR_PRIMARY_BUTTON : CALENDAR_OUTLINE_BUTTON
              }
              data-cy="provider-calendar-editor-detailed"
              onClick={() => {
                setMode('SHIFT_DETAIL');
                if (drafts.length === 0) addDraft();
              }}
              type="button"
            >
              Shift details
            </button>
          </div>
        </fieldset>

        {mode === 'DATE_ONLY' ? (
          <div className="space-y-3">
            <p className="rounded-md bg-grey-950 p-3 text-sm text-grey-400">
              Date-only availability proves date overlap without claiming a specific shift or call
              schedule.
            </p>
            <fieldset className="grid gap-3 sm:grid-cols-2" disabled={editingDisabled}>
              <legend className="sr-only">Optional date-only details</legend>
              <label className="text-sm text-grey-400">
                Work location
                <select
                  className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                  data-cy="provider-calendar-date-only-work-location"
                  onChange={(event) =>
                    setDateOnlyDraft((current) => ({
                      ...current,
                      workLocation: event.target.value as WorkLocation | '',
                    }))
                  }
                  value={dateOnlyDraft.workLocation}
                >
                  <option value="">Not specified</option>
                  <option value="ONSITE">Onsite</option>
                  <option value="PARTIAL_REMOTE">Hybrid</option>
                  <option value="REMOTE">Remote / telemedicine</option>
                </select>
              </label>
              <label className="text-sm text-grey-400">
                State
                <UsStateSelect
                  className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                  data-cy="provider-calendar-date-only-state"
                  onChange={(stateCode) =>
                    setDateOnlyDraft((current) => ({ ...current, stateCode }))
                  }
                  value={dateOnlyDraft.stateCode}
                />
              </label>
              <label className="text-sm text-grey-400 sm:col-span-2">
                Private note
                <textarea
                  className="mt-1 min-h-20 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                  data-cy="provider-calendar-date-only-notes"
                  onChange={(event) =>
                    setDateOnlyDraft((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                  value={dateOnlyDraft.notes}
                />
              </label>
            </fieldset>
          </div>
        ) : (
          <div className="space-y-4">
            {drafts.map((draft, index) => (
              <fieldset
                className="rounded-lg border border-grey-900 p-4"
                data-cy={`provider-calendar-editor-entry-${index}`}
                disabled={editingDisabled}
                key={draft.clientId}
              >
                <div className="flex items-center justify-between">
                  <legend className="body-text-sb text-grey-100">Window {index + 1}</legend>
                  <button
                    aria-label={`Remove window ${index + 1}`}
                    className="rounded p-1 text-grey-500 hover:bg-grey-900 hover:text-error"
                    onClick={() =>
                      setDrafts((current) =>
                        current.filter((item) => item.clientId !== draft.clientId)
                      )
                    }
                    type="button"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-grey-500">Times shown in {draft.timezone}.</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="text-sm text-grey-400">
                    Shift template
                    <select
                      className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      onChange={(event) => {
                        const shiftLabel = event.target.value as ShiftLabel;
                        updateDraft(draft.clientId, {
                          shiftLabel,
                          ...SHIFT_TEMPLATES[shiftLabel],
                        });
                      }}
                      value={draft.shiftLabel}
                    >
                      {(['DAY', 'EVENING', 'NIGHT', 'CUSTOM'] as ShiftLabel[]).map((label) => (
                        <option key={label} value={label}>
                          {label.charAt(0) + label.slice(1).toLowerCase()}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm text-grey-400">
                    Shift type
                    <select
                      className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      onChange={(event) =>
                        updateDraft(draft.clientId, {
                          shiftType: event.target.value as ShiftType,
                        })
                      }
                      value={draft.shiftType}
                    >
                      <option value="SHIFT">Active shift</option>
                      <option value="CALL">On call</option>
                    </select>
                  </label>
                  <label className="text-sm text-grey-400">
                    Start time
                    <input
                      className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      onChange={(event) =>
                        updateDraft(draft.clientId, { startTime: event.target.value })
                      }
                      type="time"
                      value={draft.startTime}
                    />
                  </label>
                  <label className="text-sm text-grey-400">
                    End time
                    <input
                      className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      onChange={(event) =>
                        updateDraft(draft.clientId, { endTime: event.target.value })
                      }
                      type="time"
                      value={draft.endTime}
                    />
                  </label>
                  <label className="text-sm text-grey-400">
                    Work location
                    <select
                      className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      onChange={(event) =>
                        updateDraft(draft.clientId, {
                          workLocation: event.target.value as WorkLocation | '',
                        })
                      }
                      value={draft.workLocation}
                    >
                      <option value="">Not specified</option>
                      <option value="ONSITE">Onsite</option>
                      <option value="PARTIAL_REMOTE">Hybrid</option>
                      <option value="REMOTE">Remote / telemedicine</option>
                    </select>
                  </label>
                  <label className="text-sm text-grey-400">
                    State
                    <UsStateSelect
                      className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      data-cy="provider-calendar-shift-state"
                      onChange={(stateCode) => updateDraft(draft.clientId, { stateCode })}
                      value={draft.stateCode}
                    />
                  </label>
                  <label className="text-sm text-grey-400 sm:col-span-2">
                    Private note
                    <textarea
                      className="mt-1 min-h-20 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                      onChange={(event) =>
                        updateDraft(draft.clientId, { notes: event.target.value })
                      }
                      value={draft.notes}
                    />
                  </label>
                </div>
              </fieldset>
            ))}
            <button
              className={CALENDAR_OUTLINE_BUTTON}
              data-cy="provider-calendar-editor-add-entry"
              disabled={editingDisabled}
              onClick={addDraft}
              type="button"
            >
              <PlusIcon className="size-4" /> Add another window
            </button>
          </div>
        )}

        {validationError ? (
          <p aria-live="assertive" className="text-sm text-error" role="alert">
            {validationError}
          </p>
        ) : null}

        {!validationError && (conflict || mutationError) ? (
          <p aria-live="assertive" className="text-sm text-error" role="alert">
            {conflict
              ? `${conflict.message} Locked entries: ${conflict.entryIds.join(', ') || 'unknown'}.`
              : mutationError}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-grey-900 pt-4">
          {editorMode === 'single' ? (
            <button
              className={CALENDAR_OUTLINE_BUTTON}
              data-cy="provider-calendar-editor-clear"
              disabled={editingDisabled}
              onClick={onClear}
              type="button"
            >
              Clear editable
            </button>
          ) : null}
          <button
            className={CALENDAR_PRIMARY_BUTTON}
            data-cy="provider-calendar-editor-save"
            disabled={editingDisabled}
            onClick={handleSave}
            type="button"
          >
            {busy ? 'Saving…' : editorMode === 'template' ? 'Save all' : 'Save day'}
          </button>
        </div>
      </div>
    </SlideInPanel>
  );
};
